// ANANT SMART MARKETING — Business Dashboard
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../utils/api';
import ATPayCard from '../../components/ATPayCard';
import ATWalletBadge from '../../components/ATWalletBadge';
import HoldToSpeak from '../../components/HoldToSpeak';
import ASMStoryKit from './ASMStoryKit';

const F = ({ label, ...rest }) => (
  <label className="flex flex-col text-[10px] opacity-90">
    <span className="mb-0.5">{label}</span>
    <input {...rest} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
  </label>
);

// ============ VOICE RECORDER — MediaRecorder → backend → Emergent Storage ============
function VoiceRecorder({ currentUrl, onUploaded }) {
  const [recording, setRecording] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(currentUrl || null);
  const [blob, setBlob] = useState(null);
  const [busy, setBusy] = useState(false);
  const recRef = React.useRef(null);
  const chunksRef = React.useRef([]);
  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '' });
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const b = new Blob(chunksRef.current, { type: 'audio/webm' });
        setBlob(b); setPreviewUrl(URL.createObjectURL(b));
      };
      rec.start(); recRef.current = rec; setRecording(true);
    } catch { toast.error('Mic permission denied'); }
  };
  const stop = () => { try { recRef.current?.stop(); } catch { /* noop */ } setRecording(false); };
  const upload = async () => {
    if (!blob) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', blob, 'greeter.webm');
      const r = await api.post('/asm/voice-greeter/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onUploaded(r.data.url);
      toast.success('✓ Voice greeter uploaded');
      setBlob(null);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
    finally { setBusy(false); }
  };
  const backendUrl = (u) => u && u.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="asm-voice-recorder">
      {!recording && !blob && (
        <button type="button" onClick={start} data-testid="asm-rec-start" className="px-3 py-1.5 rounded bg-red-500 text-white text-xs font-black">🎤 Record</button>
      )}
      {recording && (
        <button type="button" onClick={stop} data-testid="asm-rec-stop" className="px-3 py-1.5 rounded bg-red-600 text-white text-xs font-black animate-pulse">⏹ Stop</button>
      )}
      {blob && !recording && (
        <>
          <audio src={previewUrl} controls className="h-8 max-w-[200px]" />
          <button type="button" onClick={upload} disabled={busy} data-testid="asm-rec-upload" className="px-3 py-1.5 rounded bg-emerald-500 text-black text-xs font-black disabled:opacity-40">{busy ? '⏳ Uploading…' : '☁️ Upload'}</button>
          <button type="button" onClick={() => { setBlob(null); setPreviewUrl(currentUrl || null); }} className="text-[10px] px-2 py-1 rounded bg-slate-700 text-slate-200">↺</button>
        </>
      )}
      {!blob && !recording && currentUrl && (
        <audio src={backendUrl(currentUrl)} controls className="h-8 max-w-[200px]" data-testid="asm-current-greeter" />
      )}
    </div>
  );
}

export default function AnantSmartMarketing() {
  const [cfg, setCfg] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [tab, setTab] = useState('card');
  const load = useCallback(async () => {
    try {
      const [c, cat] = await Promise.all([api.get('/asm/config'), api.get('/asm/catalog').catch(() => ({ data: { catalog: [], limit: 10 } }))]);
      setCfg(c.data); setCatalog(cat.data?.catalog || []);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!cfg) return <div className="min-h-screen pt-20 text-center">⏳ Loading Anant Smart Marketing…</div>;
  const p = cfg.profile;
  const publicUrl = p?.slug ? `${window.location.origin}/asm/${p.slug}` : null;
  return (
    <div className="min-h-screen pt-20 bg-gradient-to-br from-slate-950 via-fuchsia-950/40 to-cyan-950 text-white" data-testid="asm-dashboard">
      <ATWalletBadge />
      <div className="max-w-4xl mx-auto px-3 pb-16">
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight">📣 Anant Smart Marketing</h1>
        <p className="text-[11px] opacity-70 mb-3">Digital business card · QR · voice greeter · WhatsApp mini-store · viral no-app share</p>
        <ATPayCard label="AT Pay Wallet · Smart Marketing" />
        <div className="mt-3" />
        {publicUrl && (
          <div className="rounded-2xl border-2 border-emerald-400 bg-emerald-500/10 p-3 mb-3" data-testid="asm-public-link">
            <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black mb-1">🌐 Your Public Card</div>
            <div className="flex items-center gap-2 flex-wrap">
              <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="asm-goto-public" className="flex-1 text-xs font-black truncate text-emerald-100">{publicUrl}</a>
              <button onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success('Copied'); }} className="px-2 py-1 rounded bg-slate-700 text-white text-[10px] font-black">Copy</button>
              <a href={`https://wa.me/?text=${encodeURIComponent(`Check out my digital card 🌟\n${publicUrl}`)}`} target="_blank" rel="noreferrer" className="px-2 py-1 rounded bg-green-500 text-black text-[10px] font-black">📱 Share on WA</a>
              <a href={`https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(publicUrl)}`} target="_blank" rel="noreferrer" data-testid="asm-view-qr" className="px-2 py-1 rounded bg-fuchsia-500 text-white text-[10px] font-black">📱 View QR</a>
            </div>
            <div className="text-[10px] opacity-80 mt-1">👀 {p.views || 0} views · 💾 {p.saves || 0} saves · ↗ {p.shares || 0} shares · plan <b>{p.plan_tier || 'none — subscribe to activate'}</b></div>
          </div>
        )}
        <div className="grid grid-cols-5 gap-1 p-1 rounded-2xl border border-slate-700 bg-slate-900/70 mb-4">
          {[['card','🎴 Card'],['catalog','📦 Catalog'],['story','🎨 Story'],['plans','💎 Plans'],['share','🚀 Share']].map(([id,l]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`asm-tab-${id}`} className={`px-2 py-1.5 rounded text-[11px] font-black ${tab === id ? 'bg-fuchsia-500 text-white' : 'text-slate-300 hover:bg-slate-800'}`}>{l}</button>
          ))}
        </div>
        {tab === 'card' && <CardTab cfg={cfg} onReload={load} />}
        {tab === 'catalog' && <CatalogTab items={catalog} limit={cfg.plans.find((pl) => pl.id === p?.plan_tier)?.catalog_limit || 0} onReload={load} />}
        {tab === 'story' && <ASMStoryKit profile={p} publicUrl={publicUrl} />}
        {tab === 'plans' && <PlansTab cfg={cfg} onReload={load} />}
        {tab === 'share' && <ShareTab publicUrl={publicUrl} p={p} />}
      </div>
    </div>
  );
}

function CardTab({ cfg, onReload }) {
  const p = cfg.profile || {};
  const [form, setForm] = useState({
    business_name: p.business_name || '', owner_name: p.owner_name || '', phone: p.phone || '',
    whatsapp_number: p.whatsapp_number || '', email: p.email || '', address: p.address || '', city: p.city || '',
    latitude: p.latitude || null, longitude: p.longitude || null,
    logo_url: p.logo_url || '', cover_url: p.cover_url || '',
    voice_greeter_url: p.voice_greeter_url || '', voice_greeter_text: p.voice_greeter_text || '',
    festive_banner_url: p.festive_banner_url || '', festive_message: p.festive_message || '',
    theme_color: p.theme_color || '#0ea5e9', tagline: p.tagline || '', website_url: p.website_url || '',
    social: p.social || {}, active: p.active !== false,
  });
  const [busy, setBusy] = useState(false);
  const gps = () => navigator.geolocation?.getCurrentPosition((pos) => setForm((f) => ({ ...f, latitude: pos.coords.latitude, longitude: pos.coords.longitude })));
  const save = async () => {
    if (!form.business_name) { toast.error('Business name required'); return; }
    setBusy(true);
    try { await api.post('/asm/profile', form); toast.success('✓ Card saved'); onReload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const S = (k) => (v) => setForm({ ...form, [k]: v.target ? v.target.value : v });
  return (
    <div className="rounded-2xl border-2 border-fuchsia-400/40 bg-slate-900/70 p-3 space-y-2" data-testid="asm-card-tab">
      <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-black">🎴 Business Card Details</div>
      <div className="grid grid-cols-2 gap-2">
        <F label="Business Name *" placeholder="Anant Chai Wala" value={form.business_name} onChange={S('business_name')} data-testid="asm-biz-name" />
        <F label="Owner Name" placeholder="Ramesh" value={form.owner_name} onChange={S('owner_name')} />
        <F label="Phone" value={form.phone} onChange={S('phone')} data-testid="asm-phone" />
        <F label="WhatsApp #" value={form.whatsapp_number} onChange={S('whatsapp_number')} data-testid="asm-wa" />
        <F label="Email" value={form.email} onChange={S('email')} />
        <F label="Website" value={form.website_url} onChange={S('website_url')} />
        <F label="Tagline" value={form.tagline} onChange={S('tagline')} />
        <F label="City" value={form.city} onChange={S('city')} />
      </div>
      <F label="Address" value={form.address} onChange={S('address')} />
      <button onClick={gps} data-testid="asm-gps" className="w-full px-3 py-1.5 rounded bg-slate-800 text-cyan-200 text-xs font-black">📍 {form.latitude ? `${form.latitude.toFixed(3)}, ${form.longitude.toFixed(3)}` : 'Save GPS Location'}</button>
      <div className="grid grid-cols-2 gap-2">
        <F label="Logo URL" value={form.logo_url} onChange={S('logo_url')} data-testid="asm-logo" />
        <F label="Cover URL" value={form.cover_url} onChange={S('cover_url')} />
      </div>
      <div className="rounded-xl border border-amber-400/40 bg-amber-500/10 p-2">
        <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black mb-1">🎤 Voice Greeter (auto-plays on card open)</div>
        <VoiceRecorder currentUrl={form.voice_greeter_url} onUploaded={(url) => setForm({ ...form, voice_greeter_url: url })} />
        <div className="grid grid-cols-1 gap-2 mt-2">
          <F label="Or paste external audio URL" value={form.voice_greeter_url} onChange={S('voice_greeter_url')} data-testid="asm-voice-url" />
          <F label="Or plain text (TTS fallback)" value={form.voice_greeter_text} onChange={S('voice_greeter_text')} placeholder="Namaste, welcome to Anant Chai Wala…" data-testid="asm-voice-text" />
        </div>
      </div>
      <div className="rounded-xl border border-pink-400/40 bg-pink-500/10 p-2">
        <div className="text-[10px] uppercase tracking-widest text-pink-300 font-black mb-1">🎉 Festive Banner</div>
        <div className="grid grid-cols-1 gap-2">
          <F label="Banner Image URL" value={form.festive_banner_url} onChange={S('festive_banner_url')} data-testid="asm-fest-url" />
          <F label="Message" value={form.festive_message} onChange={S('festive_message')} placeholder="Diwali dhamaka · 20% off till Nov 5" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <F label="Instagram" value={form.social?.ig || ''} onChange={(e) => setForm({ ...form, social: { ...form.social, ig: e.target.value } })} />
        <F label="Facebook" value={form.social?.fb || ''} onChange={(e) => setForm({ ...form, social: { ...form.social, fb: e.target.value } })} />
        <F label="YouTube" value={form.social?.yt || ''} onChange={(e) => setForm({ ...form, social: { ...form.social, yt: e.target.value } })} />
      </div>
      <F label="Theme Color" type="color" value={form.theme_color} onChange={S('theme_color')} />
      <label className="flex items-center gap-2 text-xs">
        <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Public card active
      </label>
      <button onClick={save} disabled={busy} data-testid="asm-card-save" className="w-full px-3 py-2 rounded bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white text-sm font-black disabled:opacity-40">
        {busy ? '⏳ Saving…' : '💾 Save & Publish Card'}
      </button>
    </div>
  );
}

function CatalogTab({ items, limit, onReload }) {
  const [form, setForm] = useState({ name: '', description: '', price_at: '', price_inr: '', image_url: '', category: '', active: true });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.name) { toast.error('Name required'); return; }
    setBusy(true);
    try {
      await api.post('/asm/catalog', {
        ...form,
        price_at: form.price_at ? parseFloat(form.price_at) : null,
        price_inr: form.price_inr ? parseFloat(form.price_inr) : null,
      });
      toast.success('✓ Item saved'); setForm({ name: '', description: '', price_at: '', price_inr: '', image_url: '', category: '', active: true });
      onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    await api.delete(`/asm/catalog/${id}`); onReload();
  };
  return (
    <div className="space-y-3" data-testid="asm-catalog-tab">
      <div className="rounded-2xl border border-emerald-400/40 bg-emerald-500/10 p-3">
        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">📦 Add Catalog Item · {items.length} / {limit}</div>
          <HoldToSpeak
            label="Bolo item"
            onTranscript={(text) => {
              // Parse: "item name X, category Y, price 100, AT 50, description Z"
              const t = (text || '').toLowerCase();
              const pick = (re) => { const m = t.match(re); return m ? m[1].trim() : ''; };
              const name = pick(/(?:item|naam|name)\s+(?:name\s+)?(.+?)(?=\s+(?:category|type|price|rupees|rs|inr|at|description|note|$))/i) || text.split(/[,.]/)[0]?.trim();
              const category = pick(/(?:category|type|shreni)\s+(.+?)(?=\s+(?:price|rupees|rs|inr|at|description|$))/i);
              const priceInr = pick(/(?:price|rupees|rs|inr|kimat)\s+(?:is\s+)?(\d+(?:\.\d+)?)/i);
              const priceAt = pick(/(?:at|token)\s+(\d+(?:\.\d+)?)/i);
              const desc = pick(/(?:description|note|detail)\s+(.+?)$/i);
              setForm((f) => ({
                ...f,
                name: name || f.name,
                category: category || f.category,
                price_inr: priceInr || f.price_inr,
                price_at: priceAt || f.price_at,
                description: desc || f.description,
              }));
              toast.success('✓ Item form voice-filled');
            }}
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <F label="Item Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="asm-item-name" />
          <F label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
          <F label="Price ₹" type="number" value={form.price_inr} onChange={(e) => setForm({ ...form, price_inr: e.target.value })} />
          <F label="Price AT" type="number" value={form.price_at} onChange={(e) => setForm({ ...form, price_at: e.target.value })} />
        </div>
        <F label="Image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
        <F label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <button onClick={submit} disabled={busy || items.length >= limit} data-testid="asm-item-save" className="w-full mt-2 px-3 py-2 rounded bg-emerald-500 text-black text-xs font-black disabled:opacity-40">{items.length >= limit ? `Limit reached (${limit}). Upgrade plan.` : (busy ? '⏳' : '+ Add Item')}</button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {items.map((it) => (
          <div key={it.id} className="rounded-xl border border-slate-700 bg-slate-800/60 p-2" data-testid={`asm-item-${it.id}`}>
            {it.image_url && <div className="aspect-square bg-cover bg-center rounded mb-1" style={{ backgroundImage: `url(${it.image_url})` }} />}
            <div className="text-[11px] font-black truncate">{it.name}</div>
            <div className="text-[9px] opacity-70">{it.category || ''}</div>
            <div className="text-xs font-black text-emerald-300">₹{it.price_inr || 0} · {it.price_at || 0} AT</div>
            <button onClick={() => del(it.id)} className="text-[9px] text-red-300 mt-1">✕ Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function PlansTab({ cfg, onReload }) {
  const p = cfg.profile || {};
  const [cycle, setCycle] = useState('monthly');
  const [busy, setBusy] = useState(false);

  // Compute active plan status + countdown
  const now = new Date();
  const expiryDate = p.plan_expires_at ? new Date(p.plan_expires_at) : null;
  const isActive = !!(p.plan_tier && expiryDate && expiryDate > now);
  const daysLeft = expiryDate ? Math.max(0, Math.ceil((expiryDate - now) / 86400000)) : 0;
  const canRenew = isActive && daysLeft <= 7;

  const subscribe = async (pid) => {
    const plan = cfg.plans.find((x) => x.id === pid);
    if (!plan) return;
    const price = cycle === 'yearly' ? (plan.at_year || 0) : (plan.at_month || 0);
    // Client-side guard mirrors backend idempotency
    if (isActive && p.plan_tier === pid && daysLeft > 7) {
      toast.error(`Plan already active — ${daysLeft} days left. Renewal opens in last 7 days.`);
      return;
    }
    const verb = canRenew && p.plan_tier === pid ? 'Renew' : 'Subscribe to';
    if (!window.confirm(`${verb} ${plan.name} (${cycle})? ${price} AT will be debited from your AT wallet.`)) return;
    setBusy(true);
    try {
      const r = await api.post('/asm/plans/subscribe', { plan_id: pid, cycle });
      toast.success(`✓ ${r.data.plan.name} ${r.data.renewed ? 'renewed' : 'activated'} · valid till ${new Date(r.data.expires_at).toLocaleDateString('en-IN')}`);
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="asm-plans-tab">
      {/* Active plan banner with expiry timer */}
      {isActive && (
        <div className="rounded-2xl border-2 border-emerald-400/40 bg-emerald-500/10 p-3" data-testid="asm-active-plan-banner">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">✅ ACTIVE PLAN</div>
              <div className="text-base font-black">{(cfg.plans.find((pl) => pl.id === p.plan_tier) || {}).name || p.plan_tier} · <span className="text-emerald-300">{p.plan_cycle || 'monthly'}</span></div>
              <div className="text-[10px] opacity-80 mt-0.5">Expires on <b>{expiryDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</b></div>
            </div>
            <div className={`px-3 py-2 rounded-xl font-black text-center ${daysLeft <= 7 ? 'bg-amber-500 text-black animate-pulse' : daysLeft <= 30 ? 'bg-cyan-500 text-black' : 'bg-slate-800 text-emerald-300'}`}>
              <div className="text-2xl leading-none">{daysLeft}</div>
              <div className="text-[9px] uppercase tracking-widest">days left</div>
            </div>
          </div>
          {daysLeft > 7 && (
            <div className="text-[10px] opacity-70 mt-2">Renewal button will unlock automatically in the last 7 days.</div>
          )}
          {daysLeft <= 7 && (
            <div className="text-[10px] text-amber-300 font-black mt-2">⚡ Renew now — next term extends from your current expiry so no days lost.</div>
          )}
        </div>
      )}
      {!isActive && (
        <div className="rounded-2xl border-2 border-amber-400/40 bg-amber-500/10 p-3 text-center">
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black">⚠️ NO ACTIVE PLAN</div>
          <div className="text-[11px] opacity-80 mt-1">Subscribe to Pro to publish your digital card & unlock all features.</div>
        </div>
      )}

      <div className="flex items-center gap-2 justify-center">
        <button onClick={() => setCycle('monthly')} data-testid="asm-cycle-monthly" className={`px-3 py-1.5 rounded-full text-xs font-black ${cycle==='monthly' ? 'bg-fuchsia-500 text-white' : 'bg-slate-800 text-slate-300 border border-slate-700'}`}>Monthly</button>
        <button onClick={() => setCycle('yearly')} data-testid="asm-cycle-yearly" className={`px-3 py-1.5 rounded-full text-xs font-black ${cycle==='yearly' ? 'bg-fuchsia-500 text-white' : 'bg-slate-800 text-slate-300 border border-slate-700'}`}>Yearly <span className="text-[9px] opacity-80">(save 20%)</span></button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {cfg.plans.map((pl) => {
          const price = cycle === 'yearly' ? (pl.at_year || 0) : (pl.at_month || 0);
          const suffix = cycle === 'yearly' ? '/yr' : '/mo';
          const isMine = p.plan_tier === pl.id && isActive;
          const locked = isMine && daysLeft > 7;
          const label = locked ? `🔒 Active (${daysLeft} days left)` : (canRenew && isMine ? '🔄 Renew Now' : (price === 0 ? 'Activate Free' : 'Subscribe'));
          return (
            <div key={pl.id} className={`rounded-2xl border-2 p-4 ${isMine ? 'border-emerald-400 bg-emerald-500/10' : 'border-slate-700 bg-slate-800/40'}`}>
              <div className="text-sm font-black">{pl.name}{isMine && <span className="ml-1 text-[10px] text-emerald-300">· YOUR PLAN</span>}</div>
              <div className="text-2xl font-black text-fuchsia-300 mt-1">{price} AT<span className="text-[10px] opacity-70">{suffix}</span></div>
              <div className="text-[10px] opacity-80 mt-0.5">{pl.catalog_limit} catalog items</div>
              <ul className="text-[10px] opacity-80 mt-2 space-y-0.5 list-disc pl-4">{(pl.features||[]).map((f) => <li key={f}>{f}</li>)}</ul>
              <button
                onClick={() => subscribe(pl.id)}
                disabled={busy || locked}
                data-testid={`asm-plan-sub-${pl.id}`}
                className={`w-full mt-3 px-2 py-2 rounded text-[11px] font-black ${locked ? 'bg-slate-700 text-slate-400 cursor-not-allowed' : (canRenew && isMine ? 'bg-amber-500 text-black' : 'bg-fuchsia-500 text-white')}`}
              >
                {busy ? '⏳ Please wait…' : label}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ShareTab({ publicUrl, p }) {
  const [text, setText] = useState(`Check out ${p?.business_name || 'my digital card'} 🌟\n${p?.tagline || ''}\n${publicUrl || ''}`);
  const share = (net) => {
    if (net === 'wa') window.open(`https://wa.me/?text=${encodeURIComponent(text)}`);
    else if (net === 'tg') window.open(`https://t.me/share/url?url=${encodeURIComponent(publicUrl)}&text=${encodeURIComponent(text)}`);
    else if (net === 'fb') window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(publicUrl)}`);
    else if (net === 'x') window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(publicUrl)}&text=${encodeURIComponent(text)}`);
  };
  const nets = [['wa','📱 WhatsApp','bg-green-500'],['tg','✈️ Telegram','bg-blue-400'],['fb','📘 Facebook','bg-blue-600'],['x','𝕏 X/Twitter','bg-slate-700']];
  return (
    <div className="space-y-3" data-testid="asm-share-tab">
      <textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {nets.map(([id, label, color]) => (
          <button key={id} onClick={() => share(id)} data-testid={`asm-share-${id}`} className={`px-3 py-2 rounded-xl ${color} text-white text-xs font-black`}>{label}</button>
        ))}
      </div>
      <Link to="/" className="block text-center text-xs opacity-70 mt-3">← Back to Home</Link>
    </div>
  );
}
